// Libraries
import { OnInit, OnChanges, OnDestroy, SimpleChange, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import * as lodash from 'lodash';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs/Subscription';

/**
 * `Filterable` implements a concern to manage filters.
 */
export abstract class Filterable<T> implements OnInit, OnChanges, OnDestroy {
  // Public properties
  /**
   * Disable or enable all form controls.
   */
  @Input() public disabled = false;
  /**
   * An expression that is called when the form has been changed.
   */
  @Output() public filter: EventEmitter<any> = new EventEmitter();
  /**
   * From group for filter object.
   */
  form: FormGroup | null = null;

  // Protected fields
  /**
   * Debounce time to trigger filter event.
   */
  protected debounceTime = 500;
  /**
   * Last filters emitted.
   */
  protected lastFilters = {};

  // Private properties
  /**
   * Check if filters has been save
   */
  private saveFilters = false;
  /**
   * Route params subscription.
   */
  private queryParamsSub: Subscription | null = null;

  /**
   * Creates a new `Paginable` instance.
   * @param refreshPath Check if filters has been save
   * @param route ActivatedRoute
   * @param router Service to navigate
   */
  constructor(
    protected refreshPath: boolean,
    protected route: ActivatedRoute,
    protected router: Router
  ) {
    this.saveFilters = refreshPath;
  }

  /**
   * Implements on init method.
   */
  ngOnInit() {
    if (this.saveFilters) {
      this.queryParamsSub = this.route.queryParams.subscribe(queryParams => {
        // Prepare query params
        const queryParamsFilters: T = this.prepareQueryParams(queryParams);
        // Load query params
        this.onFiltersQueryParams(queryParamsFilters);
        this.initForm();
      });
    } else {
      // Init form
      this.initForm();
    }
  }

  /**
   * Implements on destroy method
   *
   * @author Lautaro Klein <lautaro.klein@woopasoft.com>
   * @memberof ExternalUserComponent
   */
  ngOnDestroy() {
    if (this.queryParamsSub) {
      this.queryParamsSub.unsubscribe();
    }
  }

  /**
   * Implements on changes method for input values.
   */
  ngOnChanges(changes: {[propKey: string]: SimpleChange}) {
    // If disabled input value is changed, init form again
    if (changes.disabled && changes.disabled.currentValue !==  changes.disabled.previousValue) {
      this.onChangeDisabled();
    }
  }

  /**
   * Create form group object
   */
  protected abstract buildForm(): FormGroup;

  /**
   * Prepare filter to emit filter event.
   * @param data Form data
   */
  protected abstract prepareFilters(data: any): T;

  /**
   * Handles disabled input changes
   */
  protected abstract onChangeDisabled(): void;

  /**
   * Handles form data changes.
   * @param data New data
   */
  protected onValueChanged(data: any): void {
    this.onFilter(data);
  }

  /**
   * Handles generic form control data changes.
   * @param data New data
   */
  protected onGenericValueChanged(formControlName: string, value: any): void {
    if (!this.form) {
      return;
    }
    const control = this.form.get(formControlName);
    // Check if is disabled
    if (control && control.disabled) {
      return;
    }
    // Partial filter
    const filter: any = {};
    filter[formControlName] = value;
    // Partial filter
    this.onPartialFilter(filter);
  }

  /**
   * Bind value changes event.
   */
  protected bindForm() {
    if (!this.form) {
      return;
    }
    // Subscribe to form changes
    this.form.valueChanges
      .subscribe((data: any) => this.onValueChanged(data));
  }

  /**
   * Make filters.
   * @param data Filters
   */
  protected onFilter(data: any): void {
    let queryParams = this.prepareFilters(data);
    // Filter filters
    queryParams = <T>lodash.pickBy<any>(queryParams, (filter: any) => {
      // Filter only numbers, arrays, booleans and not empty strings
      return lodash.isNumber(filter) ||
        lodash.isArray(filter) ||
        lodash.isObject(filter) ||
        lodash.isBoolean(filter) ||
        (lodash.isString(filter) && !lodash.isEmpty(filter));
    });
    // Exit if filters is not set or are the same
    if (!queryParams || lodash.isEqual(queryParams, this.lastFilters)) {
      return;
    }
    if (this.refreshPath) {
       this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        replaceUrl: true
      });
    }
    // Emit filter event
    this.filter.emit(queryParams);
    // Save filters
    this.lastFilters = queryParams;
  }

  /**
   * Make partial filters.
   * @param data Partial filters
   */
  protected onPartialFilter(data: any) {
    if (!this.form) {
      return;
    }
    const filters = lodash.merge(this.form.value, data);
    // Execute filter
    this.onFilter(filters);
  }

  /**
   * Prepare query prams
   * @param queryParams Current query params
   */
  protected prepareQueryParams(queryParams: any): T {
    // TODO: Filters query params
    return <T> queryParams;
  }

  /**
   * Load query params filter
   * @param queryParamsFilters Current filters
   */
  protected onFiltersQueryParams(_queryParamsFilters: any) {}

  /**
   * Init form
   */
  protected initForm() {
    // Build form
    this.form = this.buildForm();
    // Bind form
    this.bindForm();
  }
}
